/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { AuthoringRegion } from 'src/core/types/regionTypes';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ConversationPublishSlotStoreData } from 'src/luis/core/stores/ConversationPublishSlotStore';

export type PublishSlotCardProps = {
    /**
     * The staging/production data
     */
    slotCardData: ConversationPublishSlotStoreData;

    /**
     * The current region the application is deployed in.
     */
    currentRegion: AuthoringRegion;

    /**
     * The current app culture.
     */
    appCulture?: string;

    /**
     * Whether the user allowed to modify in the settings or not
     */
    isReadOnly?: boolean;

    /**
     * Callback for when the user modify in the settings
     */
    onChange?: (isStaging: boolean, data: ConversationPublishSlotStoreData) => void;
};

const messages = defineMessages({
    productionTitle: {
        id: 'publishPane.publishSettings.productionTitle',
        defaultMessage: 'Production Slot'
    },
    stagingTitle: {
        id: 'publishPane.publishSettings.stagingTitle',
        defaultMessage: 'Staging Slot'
    },
    lastPublished: {
        id: 'publishPane.publishSettings.lastPublished',
        defaultMessage: 'Last Published:'
    },
    notPublishedYet: {
        id: 'publishPane.publishSettings.notPublishedYet',
        defaultMessage: 'Not Published yet'
    },
    sentimentAnalysis: {
        id: 'publishPane.publishSettings.sentimentAnalysis',
        defaultMessage: 'Sentiment Analysis:'
    },
    speechPriming: {
        id: 'publishPane.publishSettings.speechPriming',
        defaultMessage: 'Speech Priming:'
    },
    on: {
        id: 'publishPane.publishSettings.on',
        defaultMessage: 'On'
    },
    off: {
        id: 'publishPane.publishSettings.off',
        defaultMessage: 'Off'
    },
    changeSettings: {
        id: 'publishPane.publishSettings.changeSettings',
        defaultMessage: 'Change settings'
    },
    speechPrimingNote: {
        id: 'publishPane.publishSettings.speechPrimingNote',
        defaultMessage: 'Change settings'
    },
    notAvailable: {
        id: 'publishPane.publishSettings.notAvailable',
        defaultMessage: 'Setting not available in this region or culture.'
    }
});

const { styleComponent } = getStylist('PublishSlotCard');
const PublishDate = styleComponent<LuisTextProps>(LuisText)('PublishDate', theme => ({ color: theme.publishDateFontColor }));

/**
 * Displays the current publish slots information.
 *
 * @param props The input properties for the card.
 */
export const PublishSlotCard = (props: PublishSlotCardProps) => {
    const {
        slotCardData: { isStaging, isSentimentAnalysisOn, isSpeechPrimingOn, lastPublishedDate },
        currentRegion,
        appCulture,
        isReadOnly,
        onChange
    } = props;
    const { localize } = useLocalization();
    const [isEditMode, setEditMode] = useState(false);

    const isSentimentAnalysisDisabled = LuisConstants.Luis.Cultures.SentimentAnalysisUnsupportedCultures.some(c => c === appCulture);
    /**
     * @todo Use feature toggles for speech priming.
     */
    const isSpeechPrimingDisabled =
        currentRegion === 'chinaeast2' || LuisConstants.Luis.Cultures.SpeechPrimingUnsupportedCultures.some(c => c === appCulture);

    /**
     * Gets the setting row to render in the card.
     *
     * @param settingCondition The condition that governs whether to
     * display on or off.
     * @param settingLabel The label of the setting.
     * @param setting The setting to get the values for.
     */
    const getSettingRow = (
        settingCondition: boolean,
        settingLabel: string,
        setting: keyof ConversationPublishSlotStoreData,
        disabled: boolean = false
    ): JSX.Element =>
        isEditMode ? (
            <>
                <LuisText>{settingLabel}</LuisText>
                <LuisTooltip content={disabled && localize(messages.notAvailable)}>
                    <Toggle
                        disabled={disabled}
                        ariaLabel={settingLabel}
                        checked={settingCondition}
                        onText={localize(messages.on)}
                        offText={localize(messages.off)}
                        styles={{ root: { marginBottom: 0 } }}
                        onChange={(_, checked: boolean) => onChange(isStaging, { [setting]: checked })}
                    />
                </LuisTooltip>
            </>
        ) : (
            <>
                <Stack horizontal horizontalAlign="center" verticalAlign="center">
                    <LuisText>{settingLabel}</LuisText>
                </Stack>

                <LuisText fontWeight={500}>
                    {settingCondition === undefined ? '—' : settingCondition ? localize(messages.on) : localize(messages.off)}
                </LuisText>
            </>
        );

    return (
        <Stack tokens={{ childrenGap: 15 }}>
            <Stack tokens={{ childrenGap: 5 }}>
                <LuisText fontWeight={600}>{isStaging ? localize(messages.stagingTitle) : localize(messages.productionTitle)}</LuisText>

                <PublishDate>
                    <LuisText variant="small">{localize(messages.lastPublished)}</LuisText>
                    &nbsp;
                    <LuisText variant="small" fontWeight={500}>
                        {lastPublishedDate ? lastPublishedDate.toLocaleDateString() : localize(messages.notPublishedYet)}
                    </LuisText>
                </PublishDate>
            </Stack>

            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 50 }}>
                {getSettingRow(
                    isSentimentAnalysisOn,
                    localize(messages.sentimentAnalysis),
                    'isSentimentAnalysisOn',
                    isSentimentAnalysisDisabled
                )}
            </Stack>

            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 50 }}>
                {getSettingRow(isSpeechPrimingOn, localize(messages.speechPriming), 'isSpeechPrimingOn', isSpeechPrimingDisabled)}
            </Stack>

            {!isReadOnly && !isEditMode && <Link onClick={() => setEditMode(true)}>{localize(messages.changeSettings)}</Link>}
        </Stack>
    );
};
